import React from 'react';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function Profile() {
  const { user, profile } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [passwordLoading, setPasswordLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [profileData, setProfileData] = React.useState({
    firstName: '',
    lastName: '',
    phone: '',
    address: ''
  });
  const [passwordData, setPasswordData] = React.useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  React.useEffect(() => {
    if (!user || !profile) return;

    setProfileData({
      firstName: profile.first_name || '',
      lastName: profile.last_name || '',
      phone: profile.phone || '',
      address: profile.address || ''
    });
  }, [user, profile]);

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Update profile information
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          phone: profileData.phone,
          address: profileData.address
        })
        .eq('id', user.id);

      if (profileError) throw profileError;
      
      toast.success(t('common.profileUpdated'));
    } catch (err) {
      logger.error('Error updating profile:', err);
      setError(t('common.updateProfileError'));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setPasswordLoading(true);

    const { newPassword, confirmPassword } = passwordData;

    if (newPassword !== confirmPassword) {
      setError(t('common.passwordsDoNotMatch'));
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError(t('common.passwordTooShort'));
      setPasswordLoading(false);
      return;
    }

    try {
      // Change password
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      toast.success(t('common.passwordUpdated'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      logger.error('Error updating password:', err);
      setError(t('common.updatePasswordError'));
    } finally {
      setPasswordLoading(false);
    }
  }

  return (
    <div>
      <div className="md:grid md:grid-cols-3 md:gap-6 mb-8">
        <div className="md:col-span-1">
          <h1 className="text-2xl font-semibold text-gray-900">{t('common.profile')}</h1>
          <p className="mt-2 text-sm text-gray-500">
            {t('common.updateProfileInfo')}
          </p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          <form onSubmit={handleProfileSubmit}>
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                {error && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                      {t('common.firstName')}
                    </label>
                    <input
                      type="text"
                      name="first-name"
                      id="first-name"
                      value={profileData.firstName}
                      onChange={e => setProfileData({...profileData, firstName: e.target.value})}
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                      {t('common.lastName')}
                    </label>
                    <input
                      type="text"
                      name="last-name"
                      id="last-name"
                      value={profileData.lastName}
                      onChange={e => setProfileData({...profileData, lastName: e.target.value})}
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      {t('common.phone')}
                    </label>
                    <input
                      type="text"
                      name="phone"
                      id="phone"
                      value={profileData.phone}
                      onChange={e => setProfileData({...profileData, phone: e.target.value})}
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6">
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      {t('common.address')}
                    </label>
                    <input
                      type="text"
                      name="address"
                      id="address"
                      value={profileData.address}
                      onChange={e => setProfileData({...profileData, address: e.target.value})}
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change Section */}
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h1 className="text-2xl font-semibold text-gray-900">{t('common.changePassword')}</h1>
          <p className="mt-2 text-sm text-gray-500">
            {t('common.updateYourPassword')}
          </p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          <form onSubmit={handlePasswordSubmit}>
            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white space-y-6 sm:p-6">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6">
                    <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                      {t('common.newPassword')}
                    </label>
                    <input
                      type="password"
                      name="new-password"
                      id="new-password"
                      value={passwordData.newPassword}
                      onChange={e => setPasswordData({...passwordData, newPassword: e.target.value})}
                      autoComplete="new-password"
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="col-span-6">
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      {t('common.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      name="confirm-password"
                      id="confirm-password"
                      value={passwordData.confirmPassword}
                      onChange={e => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      autoComplete="new-password"
                      className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? t('common.updating') : t('common.updatePassword')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 